/* eslint-disable camelcase */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import analytics from '@nerdwallet/analytics';
import { useSegmentAnalytics } from '@nerdwallet/seo-analytics';

interface LinkClickedAttributes {
  entity_name: string;
  parent_event_id: string;
}

interface UseSegmentClickedProps {
  attributes: LinkClickedAttributes;
}

interface SegmentClickedProps {
  ref: React.RefObject<HTMLElement>;
}

const getElement = (ref) => (ref?.current ? ref.current : ref);

const useSegmentClicked = ({
  attributes,
}: UseSegmentClickedProps): SegmentClickedProps => {
  const segment = useSegmentAnalytics()?.analytics;

  const ref = useRef<HTMLElement>(null);

  const eventId = useMemo(() => analytics.uuid(), []);

  const previousEvent = useRef(null);
  const newEvent = useMemo(
    () => ({ eventId, ...attributes }),
    [eventId, attributes]
  );

  const stringifiedNewEvent = JSON.stringify(newEvent);

  const trackLinkClick = useCallback(() => {
    const element = getElement(ref);
    segment?.trackLink(element, 'link_clicked', newEvent);
  }, [segment, newEvent]);

  // Needed to add the click handler on the first render
  useEffect(() => {
    if (previousEvent.current === null) {
      trackLinkClick();
      previousEvent.current = newEvent;
    }
  }, [trackLinkClick, newEvent, stringifiedNewEvent]);

  return { ref };
};

export default useSegmentClicked;
